import * as Slot from '@radix-ui/react-slot';
import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '~/lib/cn';
import { variants } from '~/lib/variants';

import type { ComponentProps } from '../ComponentProps';
import { getClassNameOverride } from '../getClassNameOverride';

export type ButtonSize = 'xlarge' | 'large' | 'medium' | 'small';
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'error'
  | 'text';

const button = variants({
  base: cn(
    'inline-block',
    'leading-tight',
    'font-medium',
    '[&_i]:text-body-sm',
    'duration-300',
    'disabled:cursor-not-allowed',
  ),
  variants: {
    variant: {
      primary: cn(
        'text-strong-on-dark',
        'bg-primary-default',
        'hover:bg-primary-hover',
        'focus:bg-primary-pressed',
        'active:bg-primary-pressed',
        'disabled:text-disabled',
        'disabled:bg-primary-disabled',
        'rounded-full',
      ),
      secondary: cn(
        'text-strong',
        'bg-transparent',
        'rounded-full',
        'outline',
        'outline-1',
        'outline-default',
        'hover:[&[href]]:outline-hover',
        'hover:[&[href]]:bg-emphasis-strong',
        'enabled:hover:outline-hover',
        'enabled:hover:bg-emphasis-strong',
        'active:outline-pressed',
        'focus:outline-pressed',
        'disabled:text-disabled',
        'disabled:outline-disabled',
      ),
      tertiary: cn(
        'text-strong-on-dark',
        'underline',
        'disabled:text-disabled',
      ),
      error: cn(
        'bg-error-default hover:bg-error-hover active:bg-error-pressed',
        'disabled:bg-error-disabled disabled:text-disabled',
        'rounded-full',
      ),
      text: cn(
        'font-normal',
        'decoration-subtle',
        'underline underline-offset-4',
        'hover:decoration-2 hover:underline-offset-2 hover:decoration-strong',
        'active:decoration-1 active:underline-offset-4',
        'disabled:no-underline disabled:text-disabled',
      ),
    },
    size: {
      xlarge: cn('py-md', 'px-xl'),
      large: cn('py-sm', 'px-md'),
      medium: cn('py-xs', 'px-md'),
      small: cn('py-xs', 'px-md', 'text-body-sm', '[&_i]:text-body-xs'),
    },
  },
  compoundVariants: [
    {
      variant: 'tertiary',
      size: ['xlarge', 'large', 'medium', 'small'],
      class: ['p-0'],
    },
    {
      variant: 'text',
      size: ['xlarge', 'large', 'medium', 'small'],
      class: ['p-0'],
    },
  ],
});

interface ButtonProps
  extends ComponentProps<ButtonHTMLAttributes<HTMLButtonElement>> {
  children: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  asChild?: boolean;
}

export function Button({
  children,
  variant = 'primary',
  size = 'medium',
  asChild = false,
  ...props
}: ButtonProps) {
  const styles = button({ variant, size });
  const Component = asChild ? Slot.Slot : 'button';

  const className = getClassNameOverride(props, 'Button');

  return (
    <Component {...props} className={cn(styles, className)}>
      {children}
    </Component>
  );
}

Button.displayName = 'Button';
